import { ConfigFields } from '@app/shared/task/shared/form-service';
import { SelectTramiteService } from './select-tramite.service';
import { FlowOrigin, LocalFlow } from './storage.service';
import { Injectable } from '@angular/core';
import { CommonManage } from '@app/administration/process-automation/shared/common-manage';
import {
  Tarea,
  TareaTipo,
  TareaRol,
  Option,
  TareaEstado,
  Actividad,
  Const,
  TareaFinalizacion,
  TareaOpcionAdmin,
  TareaAsociacion,
  NameTaskType,
  TramiteEstado,
  Static,
  TareaCondicion,
  Documento,
  TramiteBase,
  VistaDinamica,
  Sede,
  Disparador
} from '@app/shared';
import { StorageService } from '@app/administration/process-automation/shared';
import { ToastrService } from 'ngx-toastr';
import { Logger, AuthenticationService } from '@app/core';
import { merge, groupBy, findIndex, find, result, omit, filter } from 'lodash';


import { ManageActivityService } from './manage-activity.service';
import { environment } from '@env/environment';
import { TaskService } from '@app/shared/task/shared/task.service';
import { HelperFlowService } from '@app/shared/services/helper-flow.service';

const log = new Logger('ManageTaskService');

interface ActivityTask {
  activity: Actividad;
  task: Tarea;
}

/**
 * Administra todo lo relacionado con las tareas,
 * en la automatización de procesos.
 *
 * @export
 * @class ManageTaskService
 * @extends {CommonManage}
 */
@Injectable()
export class ManageTaskService extends CommonManage {
  // Tipos de las tareas
  types: TareaTipo[];
  // Tipos de finalización para las tareas
  completionTypes: TareaTipo[];
  // Tipos de finalización para las secundarias
  completionTypesSecondary: TareaTipo[];
  // Tipos de los estados de las tareas
  status: TareaEstado[];
  // Tipos de los estados de los tramites
  statusTramite: TramiteEstado[];
  // Tipos de los estados de los expedientes
  statusExpediente: TramiteBase[];
  // Contiene los disparadores del sistema
  triggers: Disparador[] = [];
  // Todos los roles del sistema compatibles con las tareas
  roles: TareaRol[] = [];
  // Sedes de la empresa del usuario logueado
  sedes: Sede[] = [];
  // Contiene las vistas dinamicas del sistema
  dynamicViews: VistaDinamica[] = [];
  // Contiene el id de saltar tarea
  idCompletionSkipTask = -1;

  constructor(
    protected ss: StorageService,
    private ts: TaskService,
    private mas: ManageActivityService,
    private hfs: HelperFlowService,
    private toastr: ToastrService,
    private sts: SelectTramiteService,
    private as: AuthenticationService,
  ) {
    super();
    this.init();
  }

  async init() {
    // Consultar los posibles tipos de una tarea
    const types = await this.ts.getTypes();
    const indexTypeSecondary = findIndex(
      types,
      type => {
        return String(type.nombre).toLowerCase() === Const.TYPES_TASK.secondary.toLowerCase();
      }
    );
    if (indexTypeSecondary !== -1) {
      types.splice(indexTypeSecondary, 1);
    }
    // Eliminar tipos de tarea que no se muestran al usuario
    const notIncludeTypes = ['asignacion'];
    this.types = types.filter(el => {
      return !notIncludeTypes.includes(el.nombre);
    });
    // Consultar los posibles estados de una tarea
    this.status = await this.ts.getStatus();
    const indexStatusSecondary = findIndex(
      this.status,
      state => {
        return String(state.nombre).toLowerCase() === Const.STATUS_TASK.secondaryProgress.toLowerCase();
      }
    );
    if (indexStatusSecondary !== -1) {
      this.status.splice(indexStatusSecondary, 1);
    }
    // Consultar los posibles tipos de finalización de una tarea
    this.completionTypes = await this.ts.getCompletionTypes();
    this.completionTypesSecondary = filter(this.completionTypes, data => {
      return data.nombre === 'saltar_tarea';
    });
    const skipTask = find(this.completionTypes, {nombre: 'saltar_tarea'});
    if (skipTask) {
      this.idCompletionSkipTask = skipTask.id;
    }
    // Consultar los posibles tipos de finalización de una tarea
    this.statusTramite = await this.sts.getDefaultStatusTramite();
    // Consultar los posibles tipos de finalización de una tarea
    this.statusExpediente = await this.sts.getDefaultStatusExpediente();
    // Consultar los posibles tipos de finalización de una tarea
    this.triggers = await this.sts.getTriggers();
    // Consultar vistas dinamicas
    if (environment.vistasDinamicas) {
      this.dynamicViews = await this.sts.getDynamicViews();
    }
    // Consultar los roles del sistema y generar array de 'TareaRol'
    const rolesUsers = await this.ts.getRoles();
    rolesUsers.forEach(el => {
      this.roles.push(
        new TareaRol({
          rol_id: el.id,
          nombre: el.nombre,
          descripcion: el.descripcion
        })
      );
    });
    this.sedes = await this.ts.getSedes(this.as.getCredentials().info_persona.id_empresa);
  }

  /**
   * Renorna una lista de opciones compatibles para un select
   * generado por un formulario dinámico, para almacenar los
   * tipos de las tareas.
   *
   * @returns {Option[]}
   * @memberof ManageTaskService
   */
  getTypesOptions(): Option[] {
    const getType = this.getOptions(this.types, 'descripcion');
    getType.map(el => {
      el.value = el.value.charAt(0).toUpperCase() +
        el.value.replace('_', ' ').slice(1);
    });
    return getType;
  }

  /**
   * Renorna una lista de opciones compatibles para un select
   * generado por un formulario dinámico, para almacenar los
   * estados de las tareas.
   *
   * @returns {Option[]}
   * @memberof ManageTaskService
   */
  getStatusOptions(): Option[] {
    const getStatus = this.getOptions(this.status, 'descripcion');
    getStatus.forEach((el, index) => {
      if (el.value === 'activo') {
        getStatus.splice(index, 1);
      }
      el.value = el.value.charAt(0).toUpperCase() +
        el.value.replace('_', ' ').slice(1);
    });
    return getStatus;
  }

  /**
   * Retorna un objeto con la actividad y tarea utilizando los
   * indices de que tienen en sus respectivos arrays.
   *
   * @param {number} indexActivity
   * @param {number} indexTask
   * @returns {ActivityTask}
   * @memberof ManageTaskService
   */
  getTaskWithIndex(indexActivity: number, indexTask: number): ActivityTask {
    const activity = this.mas.activities[indexActivity],
      task = activity.tareas[indexTask];
    return {
      activity: activity,
      task: task
    };
  }

  /**
   * Detemina si una cadeana correponde al valor
   * de un tipo de tarea especifico.
   *
   * @param {number} id - id del tipo usado para comparar.
   * @param {NameTaskType} type - Tipo de tarea definido 'TYPES_TASK'.
   * @returns {boolean}
   * @memberof ManageTaskService
   */
  isType(id: number, type: NameTaskType): boolean {
    const validTypes = Const.TYPES_TASK;
    if (validTypes.hasOwnProperty(type)) {
      const _type = find(this.types, { id: Number(id) });
      if (_type) {
        return validTypes[type] === _type.nombre;
      }
    }
    return false;
  }

  /**
   * Crear una nueva tarea.
   *
   * @param {Actividad} activity
   * @param {Partial<Tarea>} data
   * @memberof ManageTaskService
   */
  new(activity: Actividad, data: Partial<Tarea>) {
    const newId = this.ss.getNextId('task');
    // Actualizar 'tarea_inicio' de las finalizaciones
    if (result(data, 'tarea_finalizacion')) {
      data.tarea_finalizacion.map(el => {
        el.tarea_inicio = newId;
      });
    }
    // Actualizar 'id_tarea_inicio' de las asociaciones
    if (result(data, 'tarea_asociacion')) {
      data.tarea_asociacion.map(el => {
        el.id_tarea_inicio = newId;
      });
    }
    // Crear formato de la tarea
    const task = merge(
      new Tarea({
        id: newId,
        orden: this.getNextOrder(activity.tareas),
        tarea_actividad: activity.id
      }),
      data
    );
    // Agregar nueva tarea
    this.add(activity, task);
    // Aplicar id usado para crear la tarea
    // Prepara el contador global para la próxima creación.
    this.ss.applyNextId('task');
    // Notificar al usuario.
    if (environment.showDebugHelpers) {
      this.toastr.success(`Tarea creada correctamente con el id: ${task.id}.`, 'Operación exitosa');
    } else {
      this.toastr.success(`Tarea creada correctamente.`, 'Operación exitosa');
    }
  }

  /**
   * Agrega una tarea al array de tareas de una actividad en el flujo.
   *
   * @param {Actividad} activity
   * @param {Tarea} task
   * @memberof ManageTaskService
   */
  add(activity: Actividad, task: Tarea) {
    // Guardar tarea en la actividad dentro del flujo
    activity.tareas.push(task);
    // Si el flujo esta sincronizado con la DB entonces se guarda
    // esta acción en un historial.
    this.ss.registerActionInHistory('tasks', 'create', {
      activityId: activity.id,
      taskId: task.id
    });
    // Ordenar tareas en forma asc en base a su key "orden"
    // Agregar metadatos
    activity.tareas = this.hfs.refineTasks(activity);
    // Guardar cambios en LocalStorage
    this.saveInLocal(activity);
  }

  /**
   * Edita la información de la tarea almacenada en una
   * determinada actividad.
   *
   * @param {Actividad} activity
   * @param {Tarea} task
   * @returns {boolean}
   * @memberof ManageTaskService
   */
  edit(activity: Actividad, task: Tarea): boolean {
    const indexOldTask = findIndex(activity.tareas, { id: task.id });
    if (indexOldTask !== -1) {
      activity.tareas[indexOldTask] = task;
      // Si la tarea es un microproceso
      if (task.tarea_tipo.nombre === Const.TYPES_TASK.microProcess) {
        this.ss.updateMicroProcess(task);
      } else {
        if (task.tarea_asociacion && task.tarea_asociacion.length) {
          task.tarea_asociacion.forEach(taskAssociation => {
            if (!taskAssociation.id_tarea_inicio) {
              taskAssociation.id_tarea_inicio = task.id;
            }
          });
        }
        if (task.tarea_finalizacion && task.tarea_finalizacion.length) {
          task.tarea_finalizacion.forEach(taskFinaization => {
            if (!taskFinaization.tarea_destino) {
              taskFinaization.tarea_destino = 0;
            }
            if (!taskFinaization.opcion) {
              taskFinaization.opcion = 0;
            }
          });
        }
        if (task.documentos && task.documentos.length) {
          task.documentos.forEach((document, index) => {
            const doc = this.purgeDocument(document);
            if (doc) {
              task.documentos[index] = doc;
            }
          });
        }
        if (task.tarea_condicion && task.tarea_condicion.length) {
          task.tarea_condicion = Static.getMapConditions(<TareaCondicion[]>task.tarea_condicion);
        }
        if (task.tarea_asociacion && task.tarea_asociacion) {
          task.tarea_asociacion.forEach((taskAssociation, index) => {
            task.tarea_asociacion[index] = this.purgeAssociation(taskAssociation);
          });
        }
      }
      // Si el flujo esta sincronizado con la DB entonces se guarda
      // esta acción en un historial.
      this.ss.registerActionInHistory('tasks', 'update', {
        activityId: activity.id,
        taskId: task.id
      }, task.id);
      if (this.mas.edit(activity, true)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Elimina una tarea especifica de una actividad.
   *
   * @param {Actividad} activity
   * @param {Tarea} task
   * @param {boolean} [notify=true]
   * @returns {boolean}
   * @memberof ManageTaskService
   */
  remove(activity: Actividad, task: Tarea, notify: boolean = true): boolean {
    const indexOldTask = findIndex(activity.tareas, {id: task.id});
    const removed = this.removeObject(activity.tareas, task.id);
    // Si la tarea fue removida
    if (removed) {
      // Reordena las tareas de la actividad
      activity.tareas = this.reorderWithIndex(activity.tareas);
      if (activity.tareas && activity.tareas.length) {
        activity.tareas.forEach((taskA, index) => {
          if (index >= indexOldTask) {
            this.edit(activity, taskA);
          }
        });
      }
      // Valida si se realiza o no la notificación de exito
      if (notify) {
        if (environment.showDebugHelpers) {
          this.toastr.success(`La tarea con el id: ${task.id} fue eliminada correctamente.`, 'Operación exitosa');
        } else {
          this.toastr.success(`La tarea fue eliminada correctamente.`, 'Operación exitosa');
        }
      }
      // Si el flujo esta sincronizado con la DB entonces se guarda
      // esta acción en un historial.
      this.ss.registerActionInHistory('tasks', 'delete', {
        activityId: activity.id,
        taskId: task.id
      }, task.id);
      this.saveInLocal(activity);
      return true;
    } else {
      if (notify) {
        log.error('No se pudó eliminar la tarea.');
      }
      return false;
    }
  }

  /**
   * Combina el array de roles responsables con el de asignaciones,
   * validando si se trata solo de un rol perteneciente a un grupo o ambos.
   *
   * @param {Array<Partial<TareaRol>>} assigned
   * @param {Array<Partial<TareaRol>>} responsibles
   * @memberof ManageTaskService
   */
  generateRoleFormat(assigned: Array<Partial<TareaRol>>, responsibles: Array<Partial<TareaRol>>): TareaRol[] {
    const groupAssigned: Array<Partial<TareaRol>> = [];
    const groupResponsibles: Array<Partial<TareaRol>> = [];
    // Definir si el rol pertenece al grupo de responsables o de asignaciones
    assigned.forEach(el => {
      groupAssigned.push({
        rol_id: el.rol_id,
        nombre: el.nombre,
        descripcion: el.descripcion,
        asignacion: true
      });
      groupAssigned[groupAssigned.length - 1]['type'] = 'assigned';
    });
    responsibles.forEach(el => {
      groupResponsibles.push({
        rol_id: el.rol_id,
        nombre: el.nombre,
        descripcion: el.descripcion,
        responsable: true
      });
      groupResponsibles[groupResponsibles.length - 1]['type'] = 'responsible';
    });
    // Concatenar todos los roles y agruparlos por 'id_rol'
    const group = groupBy([...groupAssigned, ...groupResponsibles], 'rol_id');
    const formatted = [];
    // Recorrer los grupos
    for (const key in group) {
      if (group.hasOwnProperty(key)) {
        const arr = group[key];
        let forPush;
        // Si este grupo solo contine un rol es porque no esta repetido en array
        if (group[key].length === 1) {
          forPush = arr[0];
          if (forPush['type'] === 'assigned') {
            forPush.responsable = false;
          } else {
            forPush.asignacion = false;
          }
        } else {
          // Este rol pertenece a ambos grupos(responsables o de asignaciones).
          forPush = merge(arr[0], arr[1]);
        }
        delete forPush['type'];
        formatted.push(forPush);
      }
    }
    return formatted as TareaRol[];
  }

  /**
   * Genera un array de roles para los responsables
   * y los encargados de asignar.
   *
   * @param {TareaRol[]} roles
   * @memberof ManageTaskService
   */
  destructureRoles(roles: TareaRol[]) {
    const assigned: Partial<TareaRol[]> = [],
      responsibles: Partial<TareaRol[]> = [];
    roles.forEach(el => {
      if (el.asignacion) {
        assigned.push(el);
      }
      if (el.responsable) {
        responsibles.push(el);
      }
    });
    return {
      assigned: assigned,
      responsibles: responsibles
    };
  }

  /**
   * Retorna la tarea de finalización de otra tarea.
   *
   * @param {Tarea} task
   * @param {FlowOrigin} origin
   * @param {number} [indexCompletion=0]
   * @param {TareaOpcionAdmin} [option]
   * @returns {Promise<Tarea>}
   * @memberof ManageTaskService
   */
  async getCompletionTask(
    task: Tarea,
    origin: FlowOrigin,
    indexCompletion: number = 0,
    option?: TareaOpcionAdmin ): Promise<Tarea> {
    // Consultar finalización de la tarea, si existe
    const types = Const.TYPES_TASK;
    const completions = <TareaFinalizacion[]>result(task, 'tarea_finalizacion');
    let targetTask = null;
    if (completions) {
      let targetId = 0;
      switch (task.tarea_tipo.nombre) {
        case types.form:
          if (option) {
            let comp: TareaFinalizacion;
            if (indexCompletion) {
              if (completions[indexCompletion].opcion === option.id_opcion) {
                comp = completions[indexCompletion];
              } else {
                comp = find(completions, { opcion: option.id_opcion });
              }
            } else {
              comp = find(completions, { opcion: option.id_opcion });
            }
            targetId = comp ? <number>result(comp, 'tarea_destino') : 0;
          } else {
            targetId = <number>result(completions[indexCompletion], 'tarea_destino');
          }
          break;
        case types.none:
        case types.microProcess:
          targetId = <number>result(completions[indexCompletion], 'tarea_destino');
          break;
        case types.condition:
          if (option) {
            const comp = find(completions, { opcion: option.id_opcion });
            targetId = comp ? <number>result(comp, 'tarea_destino') : 0;
          }
          break;
      }
      if (targetId !== 0) {
        let taskA: any;
        taskA = find(this.mas.allTasks, { id: targetId });
        if (taskA) {
          targetTask = taskA;
        } else {
          let res = false;
          const flowLocal = this.ss.getFromLocal([origin.tramite], 0);
          if (flowLocal) {
            if (this.hfs.flowsHistory.length > 1) {
              if (flowLocal['tramite']['actividades']) {
                const activitys = this.hfs.refineActivities(flowLocal['tramite']['actividades']);
                for (let indexA = 0; indexA < activitys.length; indexA++) {
                  const activity = this.hfs.refineTasks(activitys[indexA]);
                  taskA = find(activity, { id: targetId});
                  if (taskA) {
                    targetTask = taskA;
                    res = true;
                    break;
                  }
                }
              }
            }
            if (!res) {
              if (flowLocal['microproces']) {
                for (let indexM = 0; indexM < flowLocal['microproces'].length; indexM++) {
                  if (flowLocal['microproces'][indexM]['actividades']) {
                    const activitys = this.hfs.refineActivities(flowLocal['microproces'][indexM]['actividades']);
                    for (let indexA = 0; indexA < activitys.length; indexA++) {
                      const activity = this.hfs.refineTasks(activitys[indexA]);
                      taskA = find(activity, { id: targetId});
                      if (taskA) {
                        targetTask = taskA;
                        res = true;
                        break;
                      }
                    }
                    if (res) { break; }
                  }
                }
              }
            }
            if (!res) {
              targetTask = await this.ts.getTask(targetId);
            }
          }
        }
      }
    }
    if (targetTask) {
      if (!result(targetTask, 'meta')) {
        targetTask['meta'] = {};
      }
      if (!result(targetTask.meta, 'indexFlow')) {
        targetTask.meta['indexFlow'] = targetTask.orden;
      }
    }
    return targetTask;
  }

  /**
   * Retorna las tareas asociadas a una tarea en especifico
   *
   * @param {Tarea} task
   * @param {FlowOrigin} [origin=null]
   * @returns {Array<Tarea>}
   * @memberof ManageTaskService
   */
  getAsociationTask(task: Tarea, origin: FlowOrigin = null, associationMultiples: boolean = false): Array<Tarea> {
    const asociations = <TareaAsociacion[]>result(task, 'tarea_asociacion');
    const targetId: Array<number> = [];
    const targetTask: Array<any> = [];
    if (asociations) {
      asociations.forEach(asociation => {
        targetId.push(asociation.id_tarea_destino);
      });
    }
    if (targetId.length > 0) {
      for (let index = 0; index < targetId.length; index++) {
        let taskA: any = {};
        taskA = merge(taskA, find(this.mas.allTasks, { id: targetId[index] }));
        if (taskA && Object.keys(taskA).length) {
          if (associationMultiples) {
            const taskAssociation = find(asociations, { id_tarea_destino: targetId[index] });
            if (taskAssociation['tramite']) {
              taskA['descripcion'] = 'Trámite: ' + taskAssociation['tramite'];
              taskA['tramite'] = taskAssociation['tramite'];
            } else if (taskAssociation['task'] && taskAssociation['task']['tramite']) {
              taskA['descripcion'] = 'Trámite: ' + taskAssociation['task']['tramite'];
              taskA['tramite'] = taskAssociation['task']['tramite'];
            }
            taskA['proceso_externo'] = taskAssociation.proceso_externo;
          }
          targetTask.push(taskA);
        } else {
          let res = false;
          const flowLocal = this.ss.getFromLocal([origin.tramite], 0);
          if (flowLocal) {
            if (this.hfs.flowsHistory.length > 1) {
              if (flowLocal['tramite']['actividades']) {
                const activitys = this.hfs.refineActivities(flowLocal['tramite']['actividades']);
                for (let indexA = 0; indexA < activitys.length; indexA++) {
                  const activity = this.hfs.refineTasks(activitys[indexA]);
                  taskA = merge(taskA, find(activity, { id: targetId[index]}));
                  if (taskA && Object.keys(taskA).length) {
                    if (associationMultiples) {
                      const taskAssociation = find(asociations, { id_tarea_destino: targetId[index] });
                      if (taskAssociation['tramite']) {
                        taskA['descripcion'] = 'Trámite: ' + taskAssociation['tramite'];
                        taskA['tramite'] = taskAssociation['tramite'];
                      }  else if (taskAssociation['task'] && taskAssociation['task']['tramite']) {
                        taskA['descripcion'] = 'Trámite: ' + taskAssociation['task']['tramite'];
                        taskA['tramite'] = taskAssociation['task']['tramite'];
                      }
                      taskA['proceso_externo'] = taskAssociation.proceso_externo;
                    }
                    targetTask.push(taskA);
                    res = true;
                    break;
                  }
                }
              }
            }
            if (!res) {
              if (flowLocal['microproces']) {
                for (let indexM = 0; indexM < flowLocal['microproces'].length; indexM++) {
                  if (flowLocal['microproces'][indexM]['actividades']) {
                    const activitys = this.hfs.refineActivities(flowLocal['microproces'][indexM]['actividades']);
                    for (let indexA = 0; indexA < activitys.length; indexA++) {
                      const activity = this.hfs.refineTasks(activitys[indexA]);
                      taskA = merge(taskA, find(activity, { id: targetId[index]}));
                      if (taskA && Object.keys(taskA).length) {
                        if (associationMultiples) {
                          const taskAssociation = find(asociations, { id_tarea_destino: targetId[index] });
                          if (taskAssociation['tramite']) {
                            taskA['descripcion'] = 'Trámite: ' + taskAssociation['tramite'];
                            taskA['tramite'] = taskAssociation['tramite'];
                          } else if (taskAssociation['task'] && taskAssociation['task']['tramite']) {
                            taskA['descripcion'] = 'Trámite: ' + taskAssociation['task']['tramite'];
                            taskA['tramite'] = taskAssociation['task']['tramite'];
                          }
                          taskA['proceso_externo'] = taskAssociation.proceso_externo;
                        }
                        targetTask.push(taskA);
                        res = true;
                        break;
                      }
                    }
                    if (res) { break; }
                  }
                }
              }
            }
            if (!res) {
              asociations.forEach(asociation => {
                  if (asociation.id_tarea_destino === targetId[index]) {
                    if (asociation.nombre) {
                      asociation['id'] = asociation.id_tarea_destino;
                      asociation['meta'] = {
                        idActivity: asociation.tarea_actividad,
                        index: asociation.orden,
                        indexLast: asociation.orden,
                        isLast: false,
                        lastIndexActive: asociation.tarea_actividad,
                        isLastActive: false,
                        belongsToLastActivity: false,
                        indexFlow: asociation.orden
                      };
                      if (associationMultiples) {
                        const taskAssociation = find(asociations, { id_tarea_destino: targetId[index] });
                        if (taskAssociation['tramite']) {
                          asociation['descripcion'] = 'Trámite: ' + taskAssociation['tramite'];
                        } else if (taskAssociation['task'] && taskAssociation['task']['tramite']) {
                          asociation['descripcion'] = 'Trámite: ' + taskAssociation['task']['tramite'];
                        }
                        taskA['proceso_externo'] = taskAssociation.proceso_externo;
                      }
                      targetTask.push(asociation);
                      res = true;
                    } else {
                      asociation['task']['id'] = asociation.id_tarea_destino;
                      asociation['task']['meta'] = {
                        idActivity: asociation['task'].tarea_actividad,
                        index: asociation['task'].orden,
                        indexLast: asociation['task'].orden,
                        isLast: false,
                        lastIndexActive: asociation['task'].tarea_actividad,
                        isLastActive: false,
                        belongsToLastActivity: false,
                        indexFlow: asociation['task'].orden
                      };
                      if (associationMultiples) {
                        const taskAssociation = find(asociations, { id_tarea_destino: targetId[index] });
                        if (taskAssociation['tramite']) {
                          asociation['task']['descripcion'] = 'Trámite: ' + taskAssociation['tramite'];
                        } else if (taskAssociation['task'] && taskAssociation['task']['tramite']) {
                          asociation['task']['descripcion'] = 'Trámite: ' + taskAssociation['task']['tramite'];
                        }
                        taskA['proceso_externo'] = taskAssociation.proceso_externo;
                      }
                      targetTask.push(asociation['task']);
                      res = true;
                    }
                  }
              });
            }
          }
        }
      }
    }
    targetTask.map(taskT => {
      if (taskT) {
        if (!result(taskT, 'meta')) {
          taskT['meta'] = {};
        }
        if (!result(taskT.meta, 'indexFlow')) {
          taskT.meta['indexFlow'] = taskT.orden;
        }
      }
    });
    return targetTask;
  }

  /**
   * Retorna los controles necesarios para crear un formulario dinámico.
   *
   * @param {Partial<ConfigFieldsActivity>} config
   * @returns
   * @memberof ManageTaskService
   */
  getControlsForDynamicForm(config: Partial<ConfigFields>) {
    return this.ts.getControls(config);
  }

  /**
   *  Autoriza el almacenamiento de las tareas en actividad en LocalStorage.
   *
   * @private
   * @memberof ManageTaskService
   */
  saveInLocal(activity: Actividad) {
    this.mas.edit(activity, true);
  }

  /**
   * Renorna una lista de opciones compatibles para un select
   * generado por un formulario dinámico.
   *
   * @private
   * @param {any[]} items
   * @param {string} [value='nombre'] // nombre de la propiedad en el item pára el value
   * @returns {Option[]}
   * @memberof ManageTaskService
   */
  private getOptions(items: any[], value: string = 'nombre'): Option[] {
    return items.map(item => {
      return { key: item.id, value: item[value] };
    });
  }

  /**
   * Limpia el objeto de la tarea asociada de las claves innesesarias para enviar al backend
   *
   * @private
   * @param {TareaAsociacion} taskAssociation
   * @returns {*}
   * @memberof ManageTaskService
   */
  private purgeAssociation(taskAssociation: TareaAsociacion): any {
    let newTaskAssociation: any;
    newTaskAssociation = {
      alias: taskAssociation.alias,
      id_tarea_destino: taskAssociation.id_tarea_destino,
      id_tarea_inicio: taskAssociation.id_tarea_inicio,
      id_tipo_asociacion: taskAssociation.id_tipo_asociacion,
      tipo_asociacion: taskAssociation.tipo_asociacion,
      documentos: [],
    };
    if (taskAssociation['task']) {
      newTaskAssociation['task'] = taskAssociation['task'];
    }
    if (taskAssociation['proceso_externo']) {
      newTaskAssociation['proceso_externo'] = taskAssociation.proceso_externo;
    }
    // validacion para indificar si la tarea asociada tiene documentos asociados desde que se modifica o crea y cuando se reordena alguna tarea de la activiadad no se pierda la informacion
    if (taskAssociation.documentos_asociados != undefined && taskAssociation.documentos_asociados.length && taskAssociation.documentos && taskAssociation.documentos.length) {
      taskAssociation.documentos_asociados.forEach(element => {
        let document = find(taskAssociation.documentos, { documento: element.id_documento });
        if (document) {
          const doc = this.purgeDocument(document, true, element.funciones);
        if (doc) {
          newTaskAssociation.documentos.push(doc);
        }
        }
      });
    }else if (taskAssociation.documentos_asociados == undefined && taskAssociation.documentos && taskAssociation.documentos.length) {
      taskAssociation.documentos.map(document => {
        const doc = this.purgeDocument(document, true);
        if (doc) {
          newTaskAssociation.documentos.push(doc);
        }
      });
    }
    return newTaskAssociation;
  }

  /**
   * Limpia el objeto de los documentos de la tarea de claves innesesarias para enviar al backend
   *
   * @private
   * @param {Documento} document
   * @param {boolean} [association=false]
   * @returns {*}
   * @memberof ManageTaskService
   */
  private purgeDocument(document: Documento, association: boolean = false, functions: any = null): any {
    let newDocument = null;
    let idDocument = -1;
    if (association) {
      if (!document['id_documento']) {
        idDocument = document.documento;
      } else {
        idDocument = document['id_documento'];
      }
      if (idDocument !== -1) {
        if (functions){
          newDocument = {
            id_documento: idDocument,
            funciones: functions
          };
        }else{
            newDocument = {
              id_documento: idDocument,
              funciones: document.funciones
            };
        }
      }
    } else {
      idDocument = document.documento;
      if (idDocument !== -1) {
        newDocument = {
          clasificacion: document.clasificacion,
          tipo_documental: document.tipo_documental,
          documento: idDocument,
          funciones: document.funciones
        };
      }
    }
    return newDocument;
  }
} // End clas



// WEBPACK FOOTER //
// ./src/app/administration/process-automation/shared/manage-task.service.ts